import { useRef, useEffect, useState } from 'react';

import Head from 'design-system/Head';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionUnifiedProjectLookupDto } from '@scale/sensor-fusion-shared';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { DOUBLE_COLUMN_MIN_WIDTH } from '../constants';
import SfuBreakdownPanel from './SfuBreakdownPanel';
import SfuOverviewPanel from './SfuOverviewPanel';
import SfuProjectToolsPanel from 'react-scale/dashboard.scale.com/components/SensorFusionLookup/SfuProject/SfuProjectToolsPanel';
import SfuJSONView from './SfuJSONPanel';
import SfuTasksOverviewPanel from './SfuTasksOverviewPanel';
import SfuPipelinePanel from './SfuPipelinePanel';
import SfuBatchStatusPanel from './SfuBatchStatusPanel';
import SfuStagesPanel from './SfuStagesPanel';

type SfuProjectLookupViewProps = {
  project: ISensorFusionUnifiedProjectLookupDto; // Dto created at src/sensorFusion/legacy/api.ts
};

const SfuProjectLookupView = ({ project }: SfuProjectLookupViewProps) => {
  const myRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const { isRemoAdmin } = useRemoAdminCtx();

  useEffect(() => {
    const updateWidth = () => {
      setWidth(myRef?.current?.getBoundingClientRect().width);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const leftColumnElements = (
    <>
      <SfuOverviewPanel project={project} />
      <SfuStagesPanel stageDefinitions={project.stageDefinitions} />
      <SfuProjectToolsPanel project={project} />
      {!isRemoAdmin && <SfuJSONView project={project} />}
    </>
  );

  const rightColumnElements = (
    <>
      <SfuTasksOverviewPanel project={project} />
      <SfuBreakdownPanel project={project} />
      <SfuBatchStatusPanel batchOverview={project.batchOverview} />
      <SfuPipelinePanel project={project} />
    </>
  );

  return (
    <div className="scaleui scale-tailwind scale-theme w-full flex justify-center" ref={myRef}>
      <Head title={`SFU Project ${project.name}`} />
      <div className="flex flex-col gap-4 max-w-full">
        <Typography.H1>Unified Sensor Fusion Project</Typography.H1>
        <div>
          <span className="font-medium text-lg">{project.name} </span>
          <WithCopy className="inline-block" copyText={project._id}>
            {project._id}
          </WithCopy>
        </div>
        {width && width >= DOUBLE_COLUMN_MIN_WIDTH ? (
          <div className="flex gap-4 min-w-0">
            <div className="flex flex-col gap-4 min-w-0 w-1/3 overflow-wrap-anywhere">
              {leftColumnElements}
            </div>
            <div className="flex flex-col gap-4 min-w-0 w-2/3 overflow-wrap-anywhere">
              {rightColumnElements}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 min-w-0 overflow-wrap-anywhere">
            {leftColumnElements}
            {rightColumnElements}
          </div>
        )}
        <style jsx>{`
          .overflow-wrap-anywhere {
            overflow-wrap: anywhere;
          }
        `}</style>
      </div>
    </div>
  );
};

export default SfuProjectLookupView;
